import { useEffect, useRef, useState, type FormEvent } from 'react'
import type {
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  RecaptchaVerifier,
  UserCredential,
} from 'firebase/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TwoStepMessages } from '@/constants/messages'
import { reportAuthFailure } from '@/services/authFailureLog'
import {
  SMS,
  TOTP,
  finishWithApp,
  finishWithText,
  secondStepResolver,
  sendSignInText,
  smsVerifier,
  stepLabel,
} from '@/services/twoFactor'
import {
  sendRecoveryCode,
  verifyRecoveryCode,
  type RecoveryAccount,
} from '@/services/twoStepBackup'

interface SecondStepChallengeProps {
  /** The `auth/multi-factor-auth-required` error from sign-in or a re-check. */
  error: MultiFactorError
  onDone: (credential: UserCredential) => void
  onCancel: () => void
  /** Who is signing in. Given only on the sign-in page, where "Lost
   *  access? Use your backup email" is offered (PB-81). */
  recoveryAccount?: RecoveryAccount
}

function codeError(error: unknown): string {
  const code = (error as { code?: string })?.code
  if (
    code === 'auth/invalid-verification-code' ||
    code === 'auth/invalid-credential' ||
    code === 'auth/totp-challenge-timeout'
  )
    return TwoStepMessages.wrongCode
  if (code === 'auth/too-many-requests' || code === 'too-many')
    return TwoStepMessages.tooMany
  if (code === 'auth/code-expired' || code === 'expired')
    return TwoStepMessages.codeExpired
  if (code === 'wrong') return TwoStepMessages.wrongCode
  return TwoStepMessages.failed(code)
}

/**
 * The second sign-in step (PB-62): pick the authenticator app or a text,
 * then enter the 6-digit code. Used by the sign-in page and wherever Firebase
 * asks to re-check the user (changing email, adding a step).
 */
export function SecondStepChallenge({
  error,
  onDone,
  onCancel,
  recoveryAccount,
}: SecondStepChallengeProps) {
  const canRecover = !!(recoveryAccount?.uid || recoveryAccount?.email)
  const [resolver] = useState<MultiFactorResolver>(() =>
    secondStepResolver(error)
  )
  const hints = resolver.hints
  // The app first: it needs no text message
  const [hint, setHint] = useState<MultiFactorInfo>(
    () => hints.find(h => h.factorId === TOTP) ?? hints[0]
  )
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const verifier = useRef<RecaptchaVerifier | null>(null)
  const [recovery, setRecovery] = useState(false)

  useEffect(
    () => () => {
      verifier.current?.clear()
    },
    []
  )

  const sendText = async (h: MultiFactorInfo) => {
    setBusy(true)
    setMessage(null)
    try {
      verifier.current ??= smsVerifier('second-step-recaptcha')
      setVerificationId(await sendSignInText(resolver, h, verifier.current))
    } catch (err) {
      console.warn('[SecondStep] text failed:', err)
      reportAuthFailure('second_step_send', err)
      setMessage(codeError(err))
    } finally {
      setBusy(false)
    }
  }

  const choose = (h: MultiFactorInfo) => {
    setHint(h)
    setCode('')
    setMessage(null)
    setVerificationId(null)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setMessage(null)
    try {
      const credential =
        hint.factorId === SMS
          ? await finishWithText(resolver, verificationId as string, code)
          : await finishWithApp(resolver, hint, code)
      onDone(credential)
    } catch (err) {
      console.warn('[SecondStep] code failed:', err)
      reportAuthFailure('second_step_verify', err)
      setMessage(codeError(err))
      setBusy(false)
    }
  }

  const needsText = hint.factorId === SMS && !verificationId

  if (recovery) {
    return (
      <RecoveryStep
        account={recoveryAccount as RecoveryAccount}
        onBack={() => setRecovery(false)}
        onSignIn={onCancel}
      />
    )
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-1">
        <h2 className="text-lg font-semibold">{TwoStepMessages.title}</h2>
        <p className="text-sm text-muted-foreground">
          {hint.factorId === SMS
            ? verificationId
              ? TwoStepMessages.enterTextCode(stepLabel(hint))
              : TwoStepMessages.sendTextPrompt(stepLabel(hint))
            : TwoStepMessages.enterAppCode}
        </p>
      </div>

      {needsText ? (
        <Button type="button" disabled={busy} onClick={() => sendText(hint)}>
          {busy ? TwoStepMessages.sending : TwoStepMessages.sendCode}
        </Button>
      ) : (
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="second-step-code">
              {TwoStepMessages.codeLabel}
            </Label>
            <Input
              id="second-step-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              required
            />
          </div>
          <Button type="submit" disabled={busy || code.length !== 6}>
            {busy ? TwoStepMessages.checking : TwoStepMessages.verify}
          </Button>
        </form>
      )}

      {message && <p className="text-sm text-destructive">{message}</p>}

      {/* Primary (Verify / Send code), secondary (Cancel), then the links */}
      <Button type="button" variant="outline" onClick={onCancel}>
        {TwoStepMessages.cancel}
      </Button>

      {(hints.length > 1 || canRecover) && (
        <div className="flex flex-col items-center">
          {hints
            .filter(h => h.uid !== hint.uid)
            .map(h => (
              <Button
                key={h.uid}
                type="button"
                variant="link"
                size="sm"
                onClick={() => choose(h)}
              >
                {TwoStepMessages.switchTo(stepLabel(h))}
              </Button>
            ))}
          {canRecover && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => setRecovery(true)}
            >
              {TwoStepMessages.recoveryLink}
            </Button>
          )}
        </div>
      )}
      <div id="second-step-recaptcha" />
    </div>
  )
}

/**
 * Lost authenticator and phone (PB-81): a code to the backup email removes
 * every two-step method; the person signs in again (password still needed)
 * and must set two-step up again before using the app.
 */
function RecoveryStep({
  account,
  onBack,
  onSignIn,
}: {
  account: RecoveryAccount
  onBack: () => void
  onSignIn: () => void
}) {
  const [stage, setStage] = useState<'ask' | 'code' | 'none' | 'done'>('ask')
  const [code, setCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    setMessage(null)
    try {
      await action()
    } catch (err) {
      console.warn('[SecondStep] recovery failed:', err)
      setMessage(codeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-1">
        <h2 className="text-lg font-semibold">{TwoStepMessages.title}</h2>
        <p className="text-sm text-muted-foreground">
          {stage === 'done'
            ? TwoStepMessages.recoveryDone
            : stage === 'none'
              ? TwoStepMessages.recoveryNone
              : TwoStepMessages.recoveryPrompt}
        </p>
      </div>

      {stage === 'ask' && (
        <Button
          type="button"
          disabled={busy}
          onClick={() =>
            run(async () => {
              const result = await sendRecoveryCode(account)
              setStage(result.status === 'none' ? 'none' : 'code')
            })
          }
        >
          {busy ? TwoStepMessages.sending : TwoStepMessages.sendCode}
        </Button>
      )}

      {stage === 'code' && (
        <form
          className="grid gap-3"
          onSubmit={e => {
            e.preventDefault()
            run(async () => {
              await verifyRecoveryCode(account, code)
              setStage('done')
            })
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="second-step-recovery-code">
              {TwoStepMessages.codeLabel}
            </Label>
            <Input
              id="second-step-recovery-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              required
            />
          </div>
          <Button type="submit" disabled={busy || code.length !== 6}>
            {busy ? TwoStepMessages.checking : TwoStepMessages.verify}
          </Button>
        </form>
      )}

      {message && <p className="text-sm text-destructive">{message}</p>}

      {stage === 'done' ? (
        <Button type="button" onClick={onSignIn}>
          {TwoStepMessages.backToSignIn}
        </Button>
      ) : (
        <Button type="button" variant="outline" onClick={onBack}>
          {TwoStepMessages.cancel}
        </Button>
      )}
    </div>
  )
}
